/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback } from 'react';

import { datadogLogs } from '@datadog/browser-logs';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { fetchUser } from 'apis/accounts/users.api';
import env from 'configs/env';
import { User } from 'types/accounts/user.type';
import { getAPIServer, getService, getSession } from 'utils/storage';

const getRoles = (data: User) => {
  return data?.roles
    ?.filter((el: { region: string | null; service: any }) => {
      if (getAPIServer() && el?.region) {
        return el?.region === getAPIServer() && el?.service === getService();
      }
      return el?.service === getService();
    })
    ?.reduce((p: string[], c: { role: ConcatArray<string> }) => p?.concat(c?.role), []);
};

const useGetMe = () => {
  const { i18n } = useTranslation();

  const initializeUserSettings = useCallback(async (data: User) => {
    try {
      localStorage.setItem('neo-id', data._id);
      await i18n.changeLanguage(data.language_preference);
      if (env.datadog) {
        datadogLogs.setUser({
          id: data._id,
          name: `${data.first_name} ${data.last_name}`,
          email: data.email,
        });
      }
    } catch (e) {
      // console.log(e);
    }
  }, []);

  const transformResponse = (response: { data: { user: User } }) => {
    if (!localStorage.getItem('neo-id')) initializeUserSettings(response.data.user);
    return response.data.user;
  };

  const { data, isPending, error } = useQuery({
    queryKey: ['me'],
    queryFn: ({ signal }) => fetchUser(signal),
    refetchOnWindowFocus: false,
    refetchInterval: false,
    refetchIntervalInBackground: false,
    refetchOnMount: false,
    refetchOnReconnect: false,
    retry: 2,
    select: transformResponse,
    enabled: !!getSession(),
    retryOnMount: false,
  });

  const userId = data?._id ?? '';

  const isVerified = data?.verified;

  const loginRoles = data ? getRoles(data) : [];

  const hasEnrollments = data
    ? loginRoles?.filter((el: string) => el?.includes('ADMIN_') || el?.includes('PHYSICIAN_'))
        ?.length > 0
    : false;

  const isAdmin = data
    ? loginRoles?.filter((el: string) => el?.includes('SERVICEADMIN') || el?.includes('ADMIN_'))
        ?.length > 0
    : false;

  const isPhysician = data
    ? loginRoles?.filter((el: string) => el?.includes('SERVICEADMIN') || el?.includes('PHYSICIAN_'))
        ?.length > 0
    : false;

  const isDeveloper = data
    ? loginRoles?.filter((el: string) => el?.includes('DEVELOPER_'))?.length > 0
    : false;

  const isDemo = data
    ? loginRoles?.filter((el: string) => el?.includes('DEMO'))?.length > 0
    : false;

  const TwoFAEnabled = data?.two_fa_enabled;

  return {
    userId,
    data,
    hasEnrollments,
    isAdmin,
    isPhysician,
    isDeveloper,
    isPending,
    error,
    isVerified,
    TwoFAEnabled,
    isDemo,
    loginRoles,
  };
};

export default useGetMe;
